/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import { Add as AddIcon, CheckCircle as CheckCircleIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

/**
 * Chamber Assignment Dialog - New Schema Version
 * 
 * New Schema Structure:
 * - Chamber has `capacity: { unit: ObjectId, max_capacity: Number }`
 * - Unit has `rates: [{ rate_id, display_name, rate, description }]`
 * - Project.chamber_assignments has `assignments: [{ rate_id, quantity, start_date, end_date, status }]`
 */

const ChamberAssignDialog = ({ 
  open, 
  onClose, 
  application, 
  onSave 
}) => {
  const [chambers, setChambers] = useState([]);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [pastAssignments, setPastAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    chamber: '',
    rate_id: '',
    quantity: 0,
    start_date: new Date(),
    end_date: null,
    comment: ''
  });
  
  // Selected chamber details for display
  const [selectedChamberDetails, setSelectedChamberDetails] = useState(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editForm, setEditForm] = useState({
    rate_id: '',
    quantity: 0,
    start_date: null,
    end_date: null,
    comment: ''
  });

  // Fetch available chambers for the facility
  const fetchAvailableChambers = useCallback(async () => {
    if (!application?.facility?._id) return;
    
    try {
      const response = await axios.get(`/api/chambers?facility=${application.facility._id}`);
      const chambersData = response.data;
      
      // Filter chambers that have a unit with capacity > 0
      const validChambers = chambersData.filter(chamber => 
        chamber.capacity?.unit && chamber.capacity?.max_capacity > 0
      );
      
      setChambers(validChambers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching chambers:', error);
      toast.error('Failed to fetch available compartments');
      setLoading(false);
    }
  }, [application?.facility?._id]);

  // Fetch existing assignments for this project
  const fetchExistingAssignments = useCallback(async () => {
    if (!application?._id) return;
    
    try {
      const response = await axios.get(`/api/projects/${application._id}/assignments`);
      
      if (response.data?.assignments) {
        const active = [];
        const completed = [];
        
        response.data.assignments.forEach(ca => {
          // New schema: assignments array instead of units array
          const assignments = ca.assignments || [];
          
          const activeAssigns = assignments.filter(a => a.status === 'Active');
          const completedAssigns = assignments.filter(a => a.status === 'Completed' || a.status === 'Terminated');
          
          if (activeAssigns.length > 0) {
            active.push({
              chamber: ca.chamber,
              assignments: activeAssigns.map(a => ({
                ...a,
                chamber_id: ca.chamber._id,
                chamber_name: ca.chamber.chamber
              }))
            });
          }
          
          if (completedAssigns.length > 0) {
            completed.push({
              chamber: ca.chamber,
              assignments: completedAssigns
            });
          }
        });
        
        setExistingAssignments(active);
        setPastAssignments(completed);
      } else {
        setExistingAssignments([]);
        setPastAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching existing assignments:', error);
      toast.error('Failed to fetch existing assignments');
    }
  }, [application?._id]);

  useEffect(() => {
    if (open && application?._id) {
      fetchAvailableChambers();
      fetchExistingAssignments();
    }
  }, [open, application?._id, fetchAvailableChambers, fetchExistingAssignments]);

  // When chamber is selected, update the details
  useEffect(() => {
    if (newAssignment.chamber) {
      const chamber = chambers.find(c => c._id === newAssignment.chamber);
      setSelectedChamberDetails(chamber || null);
      // Reset rate when chamber changes
      setNewAssignment(prev => ({ ...prev, rate_id: '', quantity: 0 }));
    } else {
      setSelectedChamberDetails(null);
    }
  }, [newAssignment.chamber, chambers]);

  const handleAddNewAssignment = () => {
    setIsAddingNew(true);
    setNewAssignment({
      chamber: '',
      rate_id: '',
      quantity: 0,
      start_date: new Date(),
      end_date: null,
      comment: ''
    });
  };

  // Handle adding more space to a specific chamber
  const handleAddMoreSpace = (chamberId) => {
    setIsAddingNew(true);
    setNewAssignment({
      chamber: chamberId,
      rate_id: '',
      quantity: 0,
      start_date: new Date(),
      end_date: null,
      comment: ''
    });
    // Scroll to the assignment form
    setTimeout(() => {
      const assignSection = document.querySelector('.assign-new-section');
      if (assignSection) {
        assignSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSaveNewAssignment = async () => {
    try {
      if (!newAssignment.chamber) {
        toast.error('Please select a compartment');
        return;
      }
      if (!newAssignment.rate_id) {
        toast.error('Please select a rate');
        return;
      }
      if (!newAssignment.quantity || newAssignment.quantity <= 0) {
        toast.error('Please enter a valid quantity');
        return;
      }
      if (!newAssignment.start_date) {
        toast.error('Please select a start date');
        return;
      }

      // Use POST to create new assignment (projectAssignmentRoutes.js)
      const assignmentData = {
        chamber_id: newAssignment.chamber,
        rate_id: newAssignment.rate_id,
        quantity: parseFloat(newAssignment.quantity),
        start_date: newAssignment.start_date.toISOString().split('T')[0],
        end_date: newAssignment.end_date ? newAssignment.end_date.toISOString().split('T')[0] : null,
        comment: newAssignment.comment?.trim() || null
      };

      const response = await axios.post(`/api/projects/${application._id}/assignments`, assignmentData);

      if (response.data) {
        setIsAddingNew(false);
        setNewAssignment({
          chamber: '',
          rate_id: '',
          quantity: 0,
          start_date: new Date(),
          end_date: null,
          comment: ''
        });
        await fetchExistingAssignments();
        await fetchAvailableChambers();
        toast.success('Compartment assigned successfully');
        if (onSave) onSave(response.data.project);
        onClose();
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error(error.response?.data?.error || 'Failed to save assignment');
    }
  };

  const handleCompleteAssignment = async (chamberId, assignmentId) => {
    try {
      const loadingToastId = toast.loading('Completing assignment...');
      
      const response = await axios.patch(`/api/projects/${application._id}/assignments/${assignmentId}/complete`, {
        end_date: new Date().toISOString(),
        status: 'Completed'
      });
      
      if (response.data) {
        toast.dismiss(loadingToastId);
        await fetchExistingAssignments();
        await fetchAvailableChambers();
        toast.success('Assignment completed successfully');
        if (onSave) onSave(response.data.project);
      }
    } catch (error) {
      console.error('Error completing assignment:', error);
      toast.error(error.response?.data?.error || 'Failed to complete assignment');
    }
  };

  const handleDeleteAssignment = async (chamberId, assignmentId, rateName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the assignment for ${rateName}? This action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const loadingToastId = toast.loading('Deleting assignment...');
      
      const response = await axios.delete(`/api/projects/${application._id}/assignments/${assignmentId}`);
      
      if (response.data) {
        toast.dismiss(loadingToastId);
        await fetchExistingAssignments();
        await fetchAvailableChambers();
        toast.success('Assignment deleted successfully');
        if (onSave) onSave(response.data.project);
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error(error.response?.data?.error || 'Failed to delete assignment');
    }
  };

  // Open edit dialog with assignment data
  const handleEditAssignment = (chamberAssign, assignment) => {
    setEditingAssignment({
      ...assignment,
      chamber: chamberAssign.chamber
    });
    
    // Parse dates from ISO string
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const [datePart] = dateStr.toString().split('T');
      const [year, month, day] = datePart.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    
    setEditForm({
      rate_id: assignment.rate_id,
      quantity: assignment.quantity,
      start_date: parseDate(assignment.start_date),
      end_date: parseDate(assignment.end_date),
      comment: assignment.comment || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      if (!editForm.rate_id) {
        toast.error('Please select a rate');
        return;
      }
      if (!editForm.quantity || editForm.quantity <= 0) {
        toast.error('Please enter a valid quantity');
        return;
      }
      if (!editForm.start_date) {
        toast.error('Please select a start date');
        return;
      }

      const loadingToastId = toast.loading('Updating assignment...');
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const endDate = editForm.end_date ? new Date(editForm.end_date) : null;
      if (endDate) endDate.setHours(0, 0, 0, 0);
      
      const isEndDatePast = endDate && endDate < today;
      const isCurrentlyCompleted = editingAssignment.status === 'Completed' || editingAssignment.status === 'Terminated';
      
      let newStatus;
      if (isEndDatePast && !isCurrentlyCompleted) {
        newStatus = 'Completed';
      } else if (!isEndDatePast && isCurrentlyCompleted) {
        newStatus = 'Active';
      }

      const updateData = {
        rate_id: editForm.rate_id,
        quantity: parseFloat(editForm.quantity),
        start_date: editForm.start_date.toISOString(),
        end_date: editForm.end_date ? editForm.end_date.toISOString() : null,
        comment: editForm.comment?.trim() || null,
        ...(newStatus && { status: newStatus })
      };

      const response = await axios.put(
        `/api/projects/${application._id}/assignments/${editingAssignment._id}`,
        updateData
      );

      if (response.data) {
        toast.dismiss(loadingToastId);
        if (newStatus === 'Completed') {
          toast.success('Assignment updated and moved to completed');
        } else if (newStatus === 'Active') {
          toast.success('Assignment updated and moved to current');
        } else {
          toast.success('Assignment updated successfully');
        }
        setEditDialogOpen(false);
        setEditingAssignment(null);
        await fetchExistingAssignments();
        await fetchAvailableChambers();
        
        if (onSave && response.data.project) {
          onSave(response.data.project);
        }
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error(error.response?.data?.error || 'Failed to update assignment');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      // Parse date string directly to avoid timezone issues
      // Input is ISO format: "2025-01-01T00:00:00.000Z"
      const dateStr = typeof date === 'string' ? date : date.toISOString();
      const [datePart] = dateStr.split('T');
      const [year, month, day] = datePart.split('-');
      // Create date using local timezone with the exact date parts
      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return format(localDate, 'MMM dd, yyyy');
    } catch {
      return '-';
    }
  };

  const formatRateBasis = (basis) => {
    if (!basis) return '/ day';
    return `/ ${basis}`;
  };

  const getRateDisplayName = (chamber, rateId) => {
    const unit = chamber?.capacity?.unit;
    if (!unit?.rates) return rateId;
    const rate = unit.rates.find(r => r.rate_id === rateId);
    return rate ? `${rate.display_name} ($${rate.rate} ${formatRateBasis(rate.rate_basis)})` : rateId;
  };

  // Get soft limit for a rate in a chamber
  const getSoftLimit = (chamber, rateId) => {
    if (!chamber?.rate_limits || !rateId) return null;
    const rateLimit = chamber.rate_limits.find(rl => rl.rate_id === rateId);
    return rateLimit ? { soft_limit: rateLimit.soft_limit, note: rateLimit.note } : null;
  };

  // Get selected rate's soft limit info
  const getSelectedRateSoftLimit = () => {
    if (!selectedChamberDetails || !newAssignment.rate_id) return null;
    return getSoftLimit(selectedChamberDetails, newAssignment.rate_id);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent>
          <Typography>Loading available compartments...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        style: { maxHeight: '90vh', overflowY: 'auto' }
      }}
    >
      <DialogTitle>Compartment Assignments</DialogTitle>
      <DialogContent sx={{ padding: 3 }}>
        {/* New Assignment Section */}
        <Box sx={{ mb: 4 }} className="assign-new-section">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Assign New Compartment</Typography>
            {!isAddingNew && (
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddNewAssignment}
                variant="outlined"
                color="primary"
              >
                Assign New Compartment
              </Button>
            )}
          </Box>

          {isAddingNew && (
            <Paper sx={{ p: 3, mb: 2 }}>
              {chambers.length === 0 ? (
                <Alert severity="warning">
                  No compartments available. Make sure compartments are configured with units in Compartment Management.
                </Alert>
              ) : (
                <>
                  {/* Chamber Selection */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Compartment</InputLabel>
                    <Select
                      value={newAssignment.chamber}
                      onChange={(e) => setNewAssignment(prev => ({ ...prev, chamber: e.target.value }))}
                      label="Select Compartment"
                    >
                      {chambers.map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {c.chamber} - {c.description}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {c.capacity?.unit?.base_unit || 'Unit'} • 
                              Capacity: {c.capacity?.max_capacity || 0} • 
                              Available: {Math.max(0, c.available ?? c.capacity?.max_capacity ?? 0)}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Rate Selection - only show when chamber is selected */}
                  {selectedChamberDetails && (
                    <>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Select Rate</InputLabel>
                        <Select
                          value={newAssignment.rate_id}
                          onChange={(e) => setNewAssignment(prev => ({ ...prev, rate_id: e.target.value }))}
                          label="Select Rate"
                        >
                          {selectedChamberDetails.capacity?.unit?.rates?.map((rate) => {
                            const softLimitInfo = getSoftLimit(selectedChamberDetails, rate.rate_id);
                            return (
                              <MenuItem key={rate.rate_id} value={rate.rate_id}>
                                <Box>
                                  <Typography variant="body2">
                                    {rate.display_name}
                                    {softLimitInfo && (
                                      <Chip 
                                        label={`Limit: ${softLimitInfo.soft_limit}`} 
                                        size="small" 
                                        color="warning"
                                        sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    ${rate.rate} {formatRateBasis(rate.rate_basis)} • {rate.description || ''}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          }) || (
                            <MenuItem disabled>No rates configured</MenuItem>
                          )}
                        </Select>
                      </FormControl>

                      {/* Limit Info - show when a rate with limit is selected */}
                      {getSelectedRateSoftLimit() && (
                        <Alert severity="info" icon={false} sx={{ mb: 2, bgcolor: '#e3f2fd' }}>
                          <Typography variant="body2">
                            <strong>Limit:</strong> {getSelectedRateSoftLimit().soft_limit} {selectedChamberDetails.capacity?.unit?.base_unit}
                          </Typography>
                          {getSelectedRateSoftLimit().note && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                              Note: {getSelectedRateSoftLimit().note}
                            </Typography>
                          )}
                        </Alert>
                      )}

                      {/* Capacity Info */}
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Unit:</strong> {selectedChamberDetails.capacity?.unit?.base_unit} | 
                          <strong> Capacity:</strong> {selectedChamberDetails.capacity?.max_capacity} | 
                          <strong> Available:</strong> {Math.max(0, selectedChamberDetails.available ?? selectedChamberDetails.capacity?.max_capacity ?? 0)}
                        </Typography>
                      </Alert>

                      {/* Quantity and Dates */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                          label="Quantity"
                          type="number"
                          value={newAssignment.quantity}
                          onChange={(e) => {
                            setNewAssignment(prev => ({ ...prev, quantity: e.target.value }));
                          }}
                          inputProps={{ min: 0, step: 'any' }}
                          sx={{ flex: 1 }}
                          helperText={`Capacity: ${selectedChamberDetails.capacity?.max_capacity ?? '—'} | Available: ${Math.max(0, selectedChamberDetails.available ?? selectedChamberDetails.capacity?.max_capacity ?? 0)}`}
                        />
                        
                        <TextField
                          label="Start Date *"
                          type="date"
                          size="small"
                          fullWidth
                          value={newAssignment.start_date ? format(newAssignment.start_date, 'yyyy-MM-dd') : ''}
                          onChange={(e) => setNewAssignment(prev => ({ 
                            ...prev, 
                            start_date: e.target.value ? new Date(e.target.value + 'T12:00:00') : null 
                          }))}
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 1 }}
                        />
                        
                        <TextField
                          label="End Date (Optional)"
                          type="date"
                          size="small"
                          fullWidth
                          value={newAssignment.end_date ? format(newAssignment.end_date, 'yyyy-MM-dd') : ''}
                          onChange={(e) => setNewAssignment(prev => ({ 
                            ...prev, 
                            end_date: e.target.value ? new Date(e.target.value + 'T12:00:00') : null 
                          }))}
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 1 }}
                        />
                      </Box>

                      {/* Optional comment/note for the assignee */}
                      <TextField
                        label="Comment / Note (optional)"
                        multiline
                        minRows={2}
                        fullWidth
                        value={newAssignment.comment}
                        onChange={(e) => setNewAssignment(prev => ({ ...prev, comment: e.target.value }))}
                        placeholder="Add a message that will be shown to the project team in their notification and email."
                        sx={{ mb: 2 }}
                      />
                    </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setIsAddingNew(false)}>Cancel</Button>
                    <Button 
                      onClick={handleSaveNewAssignment} 
                      variant="contained" 
                      color="primary"
                      disabled={!newAssignment.chamber || !newAssignment.rate_id || !newAssignment.quantity}
                    >
                      Save Assignment
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Box>

        {/* Current Assignments Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Current Assignments</Typography>
          {existingAssignments.length > 0 ? (
            existingAssignments.map((chamberAssign, index) => (
              <TableContainer component={Paper} key={index} sx={{ mb: 2 }}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {chamberAssign.chamber.chamber} - {chamberAssign.chamber.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Unit: {chamberAssign.chamber.capacity?.unit?.base_unit || 'N/A'} | 
                      Capacity: {chamberAssign.chamber.capacity?.max_capacity || 0}
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddMoreSpace(chamberAssign.chamber._id)}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Add More Space
                  </Button>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rate</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chamberAssign.assignments.map((assignment, idx) => (
                      <TableRow key={assignment._id || idx}>
                        <TableCell>
                          <Chip 
                            label={getRateDisplayName(chamberAssign.chamber, assignment.rate_id)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{assignment.quantity}</TableCell>
                        <TableCell>{formatDate(assignment.start_date)}</TableCell>
                        <TableCell>{assignment.end_date ? formatDate(assignment.end_date) : 'Ongoing'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={assignment.status} 
                            size="small"
                            color={assignment.status === 'Active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap' }}>
                          {assignment.comment
                            ? <Typography variant="body2" color="textSecondary">{assignment.comment}</Typography>
                            : <Typography variant="caption" color="textSecondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit Assignment">
                              <IconButton
                                onClick={() => handleEditAssignment(chamberAssign, assignment)}
                                color="primary"
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Finish Assignment">
                              <IconButton
                                onClick={() => handleCompleteAssignment(chamberAssign.chamber._id, assignment._id)}
                                color="success"
                                size="small"
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Assignment">
                              <IconButton
                                onClick={() => handleDeleteAssignment(
                                  chamberAssign.chamber._id, 
                                  assignment._id,
                                  getRateDisplayName(chamberAssign.chamber, assignment.rate_id)
                                )}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ))
          ) : (
            <Typography color="textSecondary">No current assignments</Typography>
          )}
        </Box>

        {/* Completed Assignments Section */}
        <Box>
          <Typography variant="h6" gutterBottom>Completed Assignments</Typography>
          {pastAssignments.length > 0 ? (
            pastAssignments.map((chamberAssign, caIndex) => (
              <TableContainer component={Paper} key={caIndex} sx={{ mb: 2 }}>
                <Box sx={{ p: 2, backgroundColor: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {chamberAssign.chamber.chamber} - {chamberAssign.chamber.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Unit: {chamberAssign.chamber.capacity?.unit?.base_unit || 'N/A'} | 
                      Capacity: {chamberAssign.chamber.capacity?.max_capacity || 0}
                    </Typography>
                  </Box>
                </Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rate</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chamberAssign.assignments.map((assignment, aIndex) => (
                      <TableRow key={`${caIndex}-${aIndex}`}>
                        <TableCell>
                          <Chip 
                            label={getRateDisplayName(chamberAssign.chamber, assignment.rate_id)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{assignment.quantity}</TableCell>
                        <TableCell>{formatDate(assignment.start_date)}</TableCell>
                        <TableCell>{formatDate(assignment.end_date)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={assignment.status} 
                            size="small"
                            color="default"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap' }}>
                          {assignment.comment
                            ? <Typography variant="body2" color="textSecondary">{assignment.comment}</Typography>
                            : <Typography variant="caption" color="textSecondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit Assignment">
                            <IconButton
                              onClick={() => handleEditAssignment(chamberAssign, assignment)}
                              color="primary"
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ))
          ) : (
            <Typography color="textSecondary">No completed assignments</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Edit Assignment Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          {editingAssignment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Compartment: <strong>{editingAssignment.chamber?.chamber}</strong> - {editingAssignment.chamber?.description}
              </Typography>
              
              {/* Rate Selection */}
              <FormControl fullWidth>
                <InputLabel>Rate</InputLabel>
                <Select
                  value={editForm.rate_id}
                  onChange={(e) => setEditForm(prev => ({ ...prev, rate_id: e.target.value }))}
                  label="Rate"
                >
                  {editingAssignment.chamber?.capacity?.unit?.rates?.map((rate) => (
                    <MenuItem key={rate.rate_id} value={rate.rate_id}>
                      {rate.display_name} (${rate.rate} {formatRateBasis(rate.rate_basis)})
                    </MenuItem>
                  )) || <MenuItem disabled>No rates configured</MenuItem>}
                </Select>
              </FormControl>
              
              {/* Quantity */}
              <TextField
                label="Quantity"
                type="number"
                value={editForm.quantity}
                onChange={(e) => setEditForm(prev => ({ 
                  ...prev, 
                  quantity: e.target.value
                }))}
                inputProps={{ min: 0, step: 'any' }}
                fullWidth
                helperText={`Capacity: ${editingAssignment.chamber?.capacity?.max_capacity || 'N/A'}`}
              />
              
              {/* Start Date */}
              <TextField
                label="Start Date *"
                type="date"
                size="small"
                fullWidth
                value={editForm.start_date ? format(editForm.start_date, 'yyyy-MM-dd') : ''}
                onChange={(e) => setEditForm(prev => ({ 
                  ...prev, 
                  start_date: e.target.value ? new Date(e.target.value + 'T12:00:00') : null 
                }))}
                InputLabelProps={{ shrink: true }}
              />
              
              {/* End Date */}
              <TextField
                label="End Date (Optional)"
                type="date"
                size="small"
                fullWidth
                value={editForm.end_date ? format(editForm.end_date, 'yyyy-MM-dd') : ''}
                onChange={(e) => setEditForm(prev => ({ 
                  ...prev, 
                  end_date: e.target.value ? new Date(e.target.value + 'T12:00:00') : null 
                }))}
                InputLabelProps={{ shrink: true }}
              />

              {/* Comment / Note */}
              <TextField
                label="Comment / Note (optional)"
                multiline
                minRows={2}
                fullWidth
                value={editForm.comment}
                onChange={(e) => setEditForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Message shown to the project team in their notification and email."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ChamberAssignDialog;
